import { Formik, FormikHelpers, FormikProps } from "formik";
import moment from "moment";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BlankMediaPeriodInfo,
  BlankMediaTypeImporterApplication,
  EquipmentItemViewModel,
  ItemViewModel,
  SubmitBlankMediaImporterApplication,
} from "../../../../../generated/data-contracts";
import {
  AvisUtils,
  BlankMediaUrls,
  BlankMediaUtils,
  ErrorUtils,
  IAxiosError,
  api,
  prepareCollectionForPost,
  nullsToUndefined,
} from "../../../../../utils";
import { AvisForm, AvisLoader } from "../../../../shared/components";
import { SaveAndSubmit } from "../../../../shared/components/form";
import { AvisConfirmModalShort } from "../../../../shared/components/modal";
import { AvisContext } from "../../../../shared/context";
import { ScrollToFieldError } from "../../../../shared/formik/ScrollToFieldError";
import { BlankMediaTypeImporterApplicationForm } from "./BlankMediaTypeImporterApplicationForm";
import { validationSchemaBlankMediaImporterApplication } from "./validations";
import _ from "lodash";
import { AvisConstants } from "../../../../../data/avisConstants";

export function BlankMediaTypeImporterApplicationPage() {
  const { res } = useContext(AvisContext);
  const [error, setError] = useState<IAxiosError>();
  const [applicationData, setApplicationData] =
    useState<BlankMediaTypeImporterApplication>();
  const [isLoadingApplicationData, setIsLoadingApplicationData] =
    useState<boolean>(true);
  const [equipments, setEquipments] = useState<EquipmentItemViewModel[]>();
  const [isLoadingEquipments, setIsLoadingEquipments] = useState<boolean>(true);
  const [countries, setCountries] = useState<ItemViewModel[]>();
  const [isLoadingCountries, setIsLoadingCountries] = useState<boolean>(true);

  const [periodInfo, setPeriodInfo] = useState<BlankMediaPeriodInfo>();
  const [isLoadingPeriodInfo, setIsLoadingPeriodInfo] = useState<boolean>(true);

  const [professionalUsers, setProfessionalUsers] = useState<ItemViewModel[]>();
  const [isLoadingProfessionalUsers, setIsLoadingProfessionalUsers] =
    useState<boolean>(false);
  const [manufacturers, setManufacturers] = useState<ItemViewModel[]>();
  const [isLoadingManufacturers, setIsLoadingManufacturers] =
    useState<boolean>(false);

  const [firstLoading, setFirstLoading] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [noDealAccepted, setNoDealAccepted] = useState<boolean>(false);

  const location = useLocation();
  const navigate = useNavigate();

  const urlParams = useMemo(
    () => {
      return BlankMediaUtils.getUrlParams();
    },
    //Needed to reload urlParams on location change without actually using location
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location],
  );

  const pageTitleValue = useMemo(() => {
    //If application id is provided - wait till appData loads
    if (!applicationData && !!urlParams.applicationId) return;

    if (!urlParams.applicationId)
      return res("blankMedia_LandingPage_button_NewSubmission");

    return applicationData?.documentNumber;
  }, [applicationData, res, urlParams.applicationId]);

  const hasNoPortalReport = useMemo(() => {
    const rowCheckArray = [
      applicationData?.exportedToCountries?.length === 0,
      applicationData?.importedFromCountries?.length === 0,
      applicationData?.expropriations?.length === 0,
      applicationData?.manufEquipments?.length === 0,
      applicationData?.sales?.length === 0,
      applicationData?.summaries?.length === 0,
    ];

    if (!applicationData?.blankMediaTypeId) return false;

    return BlankMediaUtils.hasNoPortalReport(
      applicationData?.portalReportStatusType,
      applicationData?.dealNotOccuredPortal,
      rowCheckArray.every((b) => b === true),
    );
  }, [applicationData]);

  const loadEquipmentsAndCountries = useCallback(() => {
    setIsLoadingEquipments(true);
    setIsLoadingCountries(true);

    api
      .classifiersGetActiveEquipments()
      .then((res) => {
        setEquipments(res.data);
      })
      .catch(setError)
      .finally(() => {
        setIsLoadingEquipments(false);
      });
    api
      .classifiersGetCountries()
      .then((res) => {
        setCountries(res.data);
      })
      .catch(setError)
      .finally(() => {
        setIsLoadingCountries(false);
      });
  }, []);

  const loadRelatedPeriodData = useCallback(
    (dateFrom?: Date, dateTo?: Date) => {
      if (!dateFrom || !dateTo) {
        setProfessionalUsers(undefined);
        setManufacturers(undefined);
        return;
      }

      setIsLoadingProfessionalUsers(true);
      setIsLoadingManufacturers(true);
      const dateFromValue = moment(dateFrom).format(
        AvisConstants.date_format_ISO,
      );
      const dateToValue = moment(dateTo).format(AvisConstants.date_format_ISO);
      api
        .blankMediaUsersGetActivePersonByType({
          DateFrom: dateFromValue,
          DateTo: dateToValue,
          BlankMediaUseTypes: ["ProfessionalUser"],
        } as any)
        .then((res) => {
          setProfessionalUsers(res.data);
        })
        .finally(() => {
          setIsLoadingProfessionalUsers(false);
        });
      api
        .blankMediaUsersGetActivePersonByType({
          DateFrom: dateFromValue,
          DateTo: dateToValue,
          BlankMediaUseTypes: ["Manufacturer"],
        } as any)
        .then((res) => {
          setManufacturers(res.data);
        })
        .finally(() => {
          setIsLoadingManufacturers(false);
        });

      api
        .blankMediaUsersGetBlankMediaPeriodInfo({
          DateFrom: dateFromValue,
          DateTo: dateToValue,
          BlankMediaUseTypes: ["Importer", "Manufacturer"],
          BlankMediaTypeId: urlParams.applicationId,
        } as any)
        .then((res) => {
          setPeriodInfo(res.data);
        })
        .finally(() => {
          setIsLoadingPeriodInfo(false);
        });
    },
    [urlParams],
  );

  useEffect(() => {
    loadEquipmentsAndCountries();
  }, [loadEquipmentsAndCountries]);

  const getInitValues = useCallback(
    (data: BlankMediaTypeImporterApplication | undefined) => {
      return data?.blankMediaTypeId === undefined
        ? {
            blankMediaTypeId: undefined,
            dealNotOccuredPortal: false,
            importedFromCountries: [],
            exportedToCountries: [],
            expropriations: [],
            manufEquipments: [],
            sales: [],
            summaries: [],
            dateFrom: "",
            dateTo: "",
            submitReport: false,
          }
        : {
            blankMediaTypeId: data?.blankMediaTypeId,
            dealNotOccuredPortal: data?.dealNotOccuredPortal || false,
            dateFrom: data.dateFrom ?? "",
            dateTo: data.dateTo ?? "",
            importedFromCountries: data?.importedFromCountries,
            exportedToCountries: data?.exportedToCountries,
            expropriations: data?.expropriations,
            manufEquipments: data?.manufEquipments,
            sales: data?.sales,
            summaries: data?.summaries,
            submitReport: false,
          };
    },
    [],
  );

  const loadApplicationData = useCallback(
    (formikHelper?: FormikHelpers<SubmitBlankMediaImporterApplication>) => {
      if (!urlParams?.applicationType) return;
      if (!urlParams?.applicationId) {
        setApplicationData(undefined);
        setIsLoadingApplicationData(false);
        setFirstLoading(false);
      }

      if (!urlParams.applicationId) return;

      setIsLoadingApplicationData(true);
      api
        .blankMediaUsersGetImporterApplicationDataById(urlParams.applicationId)
        .then((res) => {
          const dataNormalized =
            nullsToUndefined<BlankMediaTypeImporterApplication>(res.data);
          setApplicationData(dataNormalized);
          setNoDealAccepted(dataNormalized.dealNotOccuredPortal || false);
          loadRelatedPeriodData(
            moment(dataNormalized.dateFrom).toDate(),
            moment(dataNormalized.dateTo).toDate(),
          );
          if (formikHelper)
            formikHelper.resetForm({ values: getInitValues(dataNormalized) });
        })
        .catch(setError)
        .finally(() => {
          setIsLoadingApplicationData(false);
          setFirstLoading(false);
        });
    },
    [urlParams, loadRelatedPeriodData, getInitValues],
  );

  useEffect(() => {
    loadApplicationData();
  }, [loadApplicationData]);

  const isLoadingFirstTime = useMemo(() => {
    return (
      (isLoadingApplicationData || isLoadingEquipments || isLoadingCountries) &&
      firstLoading
    );
  }, [
    isLoadingApplicationData,
    isLoadingEquipments,
    isLoadingCountries,
    firstLoading,
  ]);

  const isLoadedClassifiers = useMemo(
    () => !!countries && !!equipments,
    [countries, equipments],
  );

  const isSectionsVisible = useCallback(
    (formikProps: FormikProps<SubmitBlankMediaImporterApplication>) => {
      const sectionsVisible1HasDate = !!formikProps?.values.dateFrom;
      const sectionsVisible2PeriodInfo =
        periodInfo?.personHasActiveRole && !periodInfo?.periodHasApplication;

      const sectionsVisible =
        sectionsVisible1HasDate && sectionsVisible2PeriodInfo;

      return sectionsVisible ?? false;
    },
    [periodInfo],
  );

  const renderActionButtons = useCallback(
    (
      editAllowed: boolean,
      formikProps: FormikProps<SubmitBlankMediaImporterApplication>,
    ) => {
      if (!editAllowed || isLoadingFirstTime) return;

      const noDealApproveNeeded =
        formikProps.values.dealNotOccuredPortal && !noDealAccepted;

      return (
        <SaveAndSubmit
          formikProps={formikProps}
          disabled={
            isSubmitting ||
            !isLoadedClassifiers ||
            !isSectionsVisible(formikProps) ||
            noDealApproveNeeded
          }
          setIsModalOpen={setIsModalOpen}
        />
      );
    },
    [
      isLoadingFirstTime,
      noDealAccepted,
      isSubmitting,
      isLoadedClassifiers,
      isSectionsVisible,
    ],
  );

  const handleSubmit = useCallback(
    (
      formikValues: SubmitBlankMediaImporterApplication,
      formikHelpers: FormikHelpers<SubmitBlankMediaImporterApplication>,
    ) => {
      setIsSubmitting(true);

      const values = _.cloneDeep(formikValues);
      if (values.dealNotOccuredPortal) {
        //Empty bag rows with possible incomplete data
        values.importedFromCountries = [];
        values.exportedToCountries = [];
        values.expropriations = [];
        values.manufEquipments = [];
        values.sales = [];
        values.summaries = [];
      } else {
        prepareCollectionForPost(values, "importedFromCountries");
        prepareCollectionForPost(values, "exportedToCountries");
        prepareCollectionForPost(values, "expropriations");
        prepareCollectionForPost(values, "manufEquipments");
        prepareCollectionForPost(values, "sales");
        //summaries are calculated and has no rowState
      }
      api
        .blankMediaUsersPostImporterApplicationData(values)
        .then((response) => {
          if (values.submitReport) {
            setSubmitted(true);
          }
          loadApplicationData(formikHelpers);

          ErrorUtils.showSuccessfulMessage(
            res("message_Success"),
            values.submitReport
              ? res("blankMedia_Application_notification_Submit")
              : res("blankMedia_Application_notification_Save"),
            500,
          );
          if (!urlParams.applicationId) {
            //If new application page - navigate to view with id & set bag id value
            formikHelpers.setFieldValue("blankMediaTypeId", response.data);
            navigate(
              BlankMediaUtils.getUrlWithParams(
                BlankMediaUrls.applicationViewWithId,
                { applicationId: response.data, applicationType: "Importer" },
              ),
            );
          }
        })
        .catch((err) => ErrorUtils.handleFormValidation(err, formikHelpers))
        .finally(() => {
          setIsSubmitting(false);
          setIsModalOpen(false);
        });
    },
    [loadApplicationData, navigate, res, urlParams.applicationId],
  );

  const renderForm = useCallback(
    (formikProps: FormikProps<SubmitBlankMediaImporterApplication>) => {
      const editAllowed =
        !applicationData?.blankMediaTypeId && !submitted
          ? true
          : AvisUtils.isEditablePortalReportStatusTypeInfo(
              applicationData?.portalReportStatusType,
            ) && !submitted;

      if (isLoadingFirstTime) return <AvisLoader inline />;

      return (
        <>
          <AvisConfirmModalShort
            isOpen={isModalOpen}
            content={res("blankMedia_Application_notification_ConfirmSubmit")}
            confirm={formikProps.submitForm}
            cancel={() => setIsModalOpen(false)}
            title={res(
              "blankMedia_Application_notification_ConfirmSubmitTitle",
            )}
            disableControls={isSubmitting}
          />
          <BlankMediaTypeImporterApplicationForm
            applicationData={applicationData}
            bag={formikProps}
            equipments={equipments}
            countries={countries}
            periodInfo={periodInfo}
            professionalUsers={professionalUsers}
            manufacturers={manufacturers}
            sectionsVisible={isSectionsVisible(formikProps)}
            noDealAccepted={noDealAccepted}
            hasNoPortalReport={hasNoPortalReport}
            setNoDealAccepted={setNoDealAccepted}
            urlParams={urlParams}
            disabled={!editAllowed}
            loading={
              isLoadingProfessionalUsers ||
              isLoadingManufacturers ||
              isLoadingPeriodInfo
            }
            onPeriodChange={loadRelatedPeriodData}
          />
          <ScrollToFieldError />
          {renderActionButtons(editAllowed, formikProps)}
        </>
      );
    },
    [
      applicationData,
      submitted,
      isLoadingFirstTime,
      isModalOpen,
      res,
      isSubmitting,
      equipments,
      countries,
      periodInfo,
      professionalUsers,
      manufacturers,
      isSectionsVisible,
      noDealAccepted,
      hasNoPortalReport,
      urlParams,
      isLoadingProfessionalUsers,
      isLoadingManufacturers,
      isLoadingPeriodInfo,
      loadRelatedPeriodData,
      renderActionButtons,
    ],
  );

  return (
    <AvisForm
      pageTitle={pageTitleValue}
      error={error}
      loading={isLoadingApplicationData}
      backLink={{
        title: res("label_Back"),
        url: BlankMediaUtils.getUrlWithParams(
          BlankMediaUrls.applicationsList,
          {},
          true,
        ),
      }}
    >
      <Formik<SubmitBlankMediaImporterApplication>
        initialValues={getInitValues(applicationData)}
        onSubmit={handleSubmit}
        validationSchema={validationSchemaBlankMediaImporterApplication(
          res,
          noDealAccepted,
        )}
        validateOnBlur={false}
        validateOnChange={false}
        enableReinitialize
      >
        {renderForm}
      </Formik>
      {isSubmitting && <AvisLoader />}
    </AvisForm>
  );
}
